// Add-to-home-screen through the <pwa-install> web component (@khmyznikov/pwa-install): on Chromium
// browsers (Chrome, Edge, Samsung Internet) it offers the real install prompt, everywhere else
// (Safari/iOS, Firefox) its own guided dialog. The component is 130 KB, so it is downloaded only
// when a screen with an install button opens (prepareInstall); what must happen early is a tiny
// listener started from main.tsx, which keeps the one-shot beforeinstallprompt for it.
import type { PWAInstallElement } from "@khmyznikov/pwa-install";
import { useEffect, useSyncExternalStore } from "react";
import { getLocale, loadLocale, translate, type Locale } from "./i18n";

/** Chromium's beforeinstallprompt event; the DOM typings do not carry it. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<unknown>;
}

const listeners = new Set<() => void>();
let started = false;
let installed = false;
let deferredPrompt: InstallPromptEvent | undefined;
let element: PWAInstallElement | undefined;
let building: Promise<PWAInstallElement> | undefined;

function notify(): void {
  for (const listener of listeners) listener();
}

function runsStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia("(display-mode: standalone)").matches;
}

function isInstallPrompt(event: Event): event is InstallPromptEvent {
  return typeof Reflect.get(event, "prompt") === "function";
}

/**
 * The component picks its dialog language from navigator.language when it connects, and has no
 * setter. The app's own language wins, so the getter says it for exactly that one synchronous
 * moment and is then put back.
 */
function connectInLocale(host: HTMLElement, el: HTMLElement, locale: Locale): void {
  const own = Object.getOwnPropertyDescriptor(navigator, "language");
  Object.defineProperty(navigator, "language", { configurable: true, get: () => locale });
  try {
    host.append(el);
  } finally {
    if (own === undefined) Reflect.deleteProperty(navigator, "language");
    else Object.defineProperty(navigator, "language", own);
  }
}

async function build(locale: Locale): Promise<PWAInstallElement> {
  // Importing the class is what registers the custom element; it also types the instance.
  const { PWAInstallElement: InstallElement } = await import("@khmyznikov/pwa-install");
  const el = document.createElement("pwa-install");
  if (!(el instanceof InstallElement)) throw new Error("<pwa-install> is not registered");
  // manual-*: the dialog opens from our install buttons only, never on its own.
  el.setAttribute("manual-apple", "");
  el.setAttribute("manual-chrome", "");
  el.setAttribute("install-description", translate(locale, "install.screen.body"));
  // Name, icon and screenshots come from the web manifest (src/webManifest.ts).
  el.setAttribute("manifest-url", `${import.meta.env.BASE_URL}manifest.webmanifest`);
  const accent = getComputedStyle(document.documentElement).getPropertyValue("--accent").trim();
  if (accent !== "") el.setAttribute("styles", JSON.stringify({ "--tint-color": accent }));
  if (deferredPrompt !== undefined) el.externalPromptEvent = deferredPrompt;
  el.addEventListener("pwa-user-choice-result-event", (event) => {
    const detail: unknown = event instanceof CustomEvent ? event.detail : undefined;
    const choice =
      typeof detail === "object" && detail !== null ? Reflect.get(detail, "message") : undefined;
    console.warn(`proef install: prompt ${typeof choice === "string" ? choice : "answered"}`);
  });
  el.addEventListener("pwa-install-success-event", () => {
    installed = true;
    notify();
  });
  connectInLocale(document.body, el, locale);
  element = el;
  return el;
}

/**
 * Called from main.tsx before the first render: the browser fires beforeinstallprompt once and
 * early, long before anyone opens an install screen. Logs, once and without any identifier,
 * whether it fired, so "never offered" can be told from "dismissed".
 */
export function startInstall(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  installed = runsStandalone();
  window.addEventListener(
    "beforeinstallprompt",
    (event) => {
      // Kept for the component (its documented "async mode"), which then owns the prompt.
      event.preventDefault();
      if (isInstallPrompt(event)) {
        deferredPrompt = event;
        if (element !== undefined) element.externalPromptEvent = event;
      }
      console.warn("proef install: beforeinstallprompt fired");
    },
    { once: true },
  );
  window.addEventListener("appinstalled", () => {
    installed = true;
    notify();
  });
}

/** Downloads and mounts the one <pwa-install>; later calls share it. */
export function prepareInstall(): Promise<PWAInstallElement> {
  building ??= build(getLocale()).catch((error: unknown) => {
    building = undefined;
    throw error;
  });
  return building;
}

/** Re-creates the component so its built-in dialog texts follow a changed app language. */
export async function refreshInstallLocale(locale: Locale): Promise<void> {
  if (building === undefined) return;
  // The component reads its description once, so the new language's dictionary must be in.
  await loadLocale(locale).catch(() => undefined);
  (await building).remove();
  element = undefined;
  building = build(locale);
  await building;
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/**
 * Whether proef already runs from the home screen, so there is nothing left to install. A screen
 * that asks also readies the component in the background, so a tap opens it within its gesture.
 */
export function useInstalled(): boolean {
  const value = useSyncExternalStore(
    subscribe,
    () => installed,
    () => false,
  );
  useEffect(() => {
    if (!value) prepareInstall().catch(() => undefined);
  }, [value]);
  return value;
}

/**
 * The tap on an install button: the real prompt where the browser offers one, otherwise the
 * component's own instructions for this browser. Always shows something.
 */
export async function showInstallDialog(): Promise<void> {
  startInstall();
  const el = await prepareInstall();
  if (el.isInstallAvailable) {
    await el.install();
    return;
  }
  console.warn("proef install: tapped, no install prompt available (guided dialog shown)");
  el.showDialog(true);
}
